angular.module('dentalLinksServices')

// CbctValidationService
// ---------------------------------------------------------------------------
// Client-side sanity check that a selection (a directory of files, a .zip, or a
// single multi-frame DICOM file) is plausibly a CBCT scan, BEFORE anything is
// uploaded. Validation is by content, not extension: CBCT slices are frequently
// extensionless, so we look for the "DICM" magic at byte offset 128.
//
// DCL-211 does not upload anything; it only vets the selection and hands back a
// summary. DCL-212 consumes validate()'s resolved value:
//   { kind, sourceName, fileCount, totalSize, files, zipFile }
//   - kind:       'directory' | 'zip' | 'single'
//   - sourceName: user-facing label (folder name, zip name, or file name)
//   - fileCount:  number of DICOM slices kept (for 'zip' this is the DICOM
//                 entry count inside the archive; for 'single' it is 1)
//   - totalSize:  uncompressed byte total of the kept DICOM payload
//   - files:      vetted File list with viewer executables excluded
//                 (empty for 'zip' — the archive itself is the payload)
//   - zipFile:    the original zip File ('zip' kind only), else null
//
// Thresholds live as named constants below so DCL-212/214 can reference them.
    .factory('CbctValidationService', ['$q', function ($q) {

        // --- Named validation thresholds --------------------------------------
        var DICM_MAGIC = 'DICM';               // DICOM preamble magic
        var DICM_MAGIC_OFFSET = 128;           // magic sits after a 128-byte preamble
        var DICM_HEADER_BYTES = 132;           // preamble + magic = bytes we must read

        var MIN_SERIES_FILE_COUNT = 20;        // a real CBCT series is many slices
        var MAX_SERIES_FILE_COUNT = 4000;      // guard against absurd selections
        var SINGLE_FILE_MIN_SIZE = 5 * 1024 * 1024;   // 5 MB: a lone multi-frame DICOM
        var MIN_SERIES_TOTAL_SIZE = 5 * 1024 * 1024;  // 5 MB: reject trivially small series
        var MIN_ZIP_SIZE = 1 * 1024 * 1024;    // 1 MB: reject trivially small zips
        var MAX_TOTAL_SIZE = 2 * 1024 * 1024 * 1024;  // 2 GB uncompressed ceiling

        var DIRECTORY_SAMPLE_SIZE = 20;        // spot-check up to N files in a directory
        var DIRECTORY_DICOM_MAJORITY = 0.6;    // >= 60% of sampled files must be DICOM

        // Filenames we tolerate quietly (metadata) but that don't count as DICOM.
        var METADATA_NAME_RE = /(^|\/)dicomdir$/i;   // DICOMDIR (extensionless index)
        var METADATA_EXT_RE = /\.(xml)$/i;           // sidecar metadata
        // Viewer executables bundled with scanner exports — excluded, not fatal.
        var EXECUTABLE_EXT_RE = /\.(exe|dll)$/i;
        var ZIP_EXT_RE = /\.zip$/i;

        // ---------------------------------------------------------------------
        // Small helpers
        // ---------------------------------------------------------------------

        function baseName(path) {
            if (!path) { return ''; }
            var parts = String(path).split(/[\\/]/);
            return parts[parts.length - 1] || parts[parts.length - 2] || String(path);
        }

        // webkitRelativePath is "<folder>/<...>/<file>"; the top segment is the folder.
        function directoryName(files) {
            for (var i = 0; i < files.length; i++) {
                var rel = files[i].webkitRelativePath;
                if (rel && rel.indexOf('/') !== -1) {
                    return rel.split('/')[0];
                }
            }
            return 'Selected folder';
        }

        function fileName(file) {
            return file.webkitRelativePath || file.name || '';
        }

        function isExecutable(name) { return EXECUTABLE_EXT_RE.test(name); }
        function isMetadata(name) {
            return METADATA_NAME_RE.test(name) || METADATA_EXT_RE.test(name);
        }

        function reject(message) {
            return $q.reject(new Error(message));
        }

        // Read the first DICM_HEADER_BYTES of a File and resolve true if the
        // "DICM" magic is present at offset 128.
        function readIsDicom(file) {
            var deferred = $q.defer();
            var blob = file.slice(0, DICM_HEADER_BYTES);
            var reader = new FileReader();
            reader.onload = function () {
                try {
                    var bytes = new Uint8Array(reader.result);
                    if (bytes.length < DICM_HEADER_BYTES) {
                        deferred.resolve(false);
                        return;
                    }
                    var magic = '';
                    for (var i = 0; i < 4; i++) {
                        magic += String.fromCharCode(bytes[DICM_MAGIC_OFFSET + i]);
                    }
                    deferred.resolve(magic === DICM_MAGIC);
                } catch (e) {
                    deferred.resolve(false);
                }
            };
            reader.onerror = function () { deferred.resolve(false); };
            reader.readAsArrayBuffer(blob);
            return deferred.promise;
        }

        // Evenly sample up to `count` items across the array (deterministic).
        function sample(items, count) {
            if (items.length <= count) { return items.slice(0); }
            var step = items.length / count;
            var out = [];
            for (var i = 0; i < count; i++) {
                out.push(items[Math.floor(i * step)]);
            }
            return out;
        }

        // ---------------------------------------------------------------------
        // ZIP central-directory parsing (no unpacking)
        // ---------------------------------------------------------------------
        // We read the End Of Central Directory record, jump to the central
        // directory, and enumerate entries (name + uncompressed size). This is
        // enough to count DICOM entries and sum uncompressed sizes without
        // inflating any data. Hand-rolled to avoid pulling a zip lib into the
        // legacy Browserify bundle.

        var EOCD_SIGNATURE = 0x06054b50;
        var CDH_SIGNATURE = 0x02014b50;
        var MAX_EOCD_SEARCH = 66000; // 64KB max comment + 22-byte record

        function readAsArrayBuffer(blob) {
            var deferred = $q.defer();
            var reader = new FileReader();
            reader.onload = function () { deferred.resolve(reader.result); };
            reader.onerror = function () { deferred.reject(new Error('Could not read the zip file.')); };
            reader.readAsArrayBuffer(blob);
            return deferred.promise;
        }

        function findEocdOffset(view) {
            var len = view.byteLength;
            var minPos = Math.max(0, len - MAX_EOCD_SEARCH);
            for (var pos = len - 22; pos >= minPos; pos--) {
                if (view.getUint32(pos, true) === EOCD_SIGNATURE) {
                    return pos;
                }
            }
            return -1;
        }

        // Parse the central directory of an already-loaded ArrayBuffer.
        // Returns { entries: [{name, size}], truncated: bool } or throws.
        function parseZipCentralDirectory(buffer) {
            var view = new DataView(buffer);
            var eocd = findEocdOffset(view);
            if (eocd === -1) {
                throw new Error('This file is not a readable zip archive.');
            }
            var entryCount = view.getUint16(eocd + 10, true);
            var cdOffset = view.getUint32(eocd + 16, true);
            var entries = [];
            var ptr = cdOffset;
            for (var i = 0; i < entryCount; i++) {
                if (ptr + 46 > view.byteLength || view.getUint32(ptr, true) !== CDH_SIGNATURE) {
                    break; // ZIP64 or corrupt — stop, use what we have
                }
                var compressedSize = view.getUint32(ptr + 20, true);
                var uncompressedSize = view.getUint32(ptr + 24, true);
                var nameLen = view.getUint16(ptr + 28, true);
                var extraLen = view.getUint16(ptr + 30, true);
                var commentLen = view.getUint16(ptr + 32, true);
                var name = '';
                for (var n = 0; n < nameLen; n++) {
                    name += String.fromCharCode(view.getUint8(ptr + 46 + n));
                }
                // Some producers omit the uncompressed size in the CDH (stored in a
                // data descriptor); fall back to the compressed size for the tally.
                var size = uncompressedSize || compressedSize || 0;
                entries.push({name: name, size: size});
                ptr += 46 + nameLen + extraLen + commentLen;
            }
            return {entries: entries, truncated: entries.length < entryCount};
        }

        // Is this entry a directory placeholder?
        function isZipDirEntry(name) {
            return /\/$/.test(name);
        }

        // ---------------------------------------------------------------------
        // Validation entry points
        // ---------------------------------------------------------------------

        function validateSingle(file) {
            if (file.size > MAX_TOTAL_SIZE) {
                return reject('This file is too large. CBCT scans must be under 2 GB.');
            }
            if (file.size < SINGLE_FILE_MIN_SIZE) {
                return reject('This file is too small to be a CBCT scan.');
            }
            return readIsDicom(file).then(function (isDicom) {
                if (!isDicom) {
                    return reject('This file is not a DICOM CBCT scan.');
                }
                return {
                    kind: 'single',
                    sourceName: baseName(fileName(file)),
                    fileCount: 1,
                    totalSize: file.size,
                    files: [file],
                    zipFile: null
                };
            });
        }

        function validateDirectory(fileList) {
            var all = Array.prototype.slice.call(fileList);
            // Drop viewer executables entirely; tolerate metadata quietly.
            var kept = [];
            var totalSize = 0;
            for (var i = 0; i < all.length; i++) {
                var name = fileName(all[i]);
                if (isExecutable(name)) { continue; }
                kept.push(all[i]);
            }
            // Candidate DICOM payload = kept files minus recognised metadata.
            var candidates = kept.filter(function (f) { return !isMetadata(fileName(f)); });

            if (candidates.length === 0) {
                return reject('No DICOM files were found in that folder.');
            }
            if (candidates.length < MIN_SERIES_FILE_COUNT) {
                return reject('That folder has too few files to be a CBCT scan (need at least ' + MIN_SERIES_FILE_COUNT + ' slices).');
            }
            if (candidates.length > MAX_SERIES_FILE_COUNT) {
                return reject('That folder has too many files to be a single CBCT scan.');
            }

            for (var j = 0; j < candidates.length; j++) {
                totalSize += candidates[j].size || 0;
            }
            if (totalSize > MAX_TOTAL_SIZE) {
                return reject('That selection is too large. CBCT scans must be under 2 GB.');
            }
            if (totalSize < MIN_SERIES_TOTAL_SIZE) {
                return reject('That folder is too small to be a CBCT scan.');
            }

            // Spot-check a sample for the DICM magic — a healthy majority must pass.
            var toCheck = sample(candidates, DIRECTORY_SAMPLE_SIZE);
            return $q.all(toCheck.map(readIsDicom)).then(function (results) {
                var dicomCount = results.filter(function (r) { return r; }).length;
                if (dicomCount / results.length < DIRECTORY_DICOM_MAJORITY) {
                    return reject('That folder does not look like a DICOM CBCT scan.');
                }
                return {
                    kind: 'directory',
                    sourceName: directoryName(all),
                    fileCount: candidates.length,
                    totalSize: totalSize,
                    files: candidates,
                    zipFile: null
                };
            });
        }

        function validateZip(file) {
            if (file.size < MIN_ZIP_SIZE) {
                return reject('That zip file is too small to be a CBCT scan.');
            }
            return readAsArrayBuffer(file).then(function (buffer) {
                var parsed = parseZipCentralDirectory(buffer);
                var dicomEntries = [];
                var totalSize = 0;
                for (var i = 0; i < parsed.entries.length; i++) {
                    var entry = parsed.entries[i];
                    if (isZipDirEntry(entry.name)) { continue; }
                    if (isExecutable(entry.name)) { continue; } // exclude viewer exes
                    if (isMetadata(entry.name)) { continue; }   // tolerate metadata
                    // Everything else is a candidate DICOM slice (extensionless or .dcm).
                    dicomEntries.push(entry);
                    totalSize += entry.size || 0;
                }

                if (dicomEntries.length === 0) {
                    return reject('No DICOM files were found in that zip.');
                }
                if (dicomEntries.length < MIN_SERIES_FILE_COUNT && dicomEntries.length !== 1) {
                    return reject('That zip has too few files to be a CBCT scan (need at least ' + MIN_SERIES_FILE_COUNT + ' slices, or one multi-frame file).');
                }
                if (dicomEntries.length > MAX_SERIES_FILE_COUNT) {
                    return reject('That zip has too many files to be a single CBCT scan.');
                }
                if (totalSize > MAX_TOTAL_SIZE) {
                    return reject('That zip is too large. CBCT scans must be under 2 GB uncompressed.');
                }
                // A single-entry zip must clear the multi-frame minimum; a series
                // must clear the series minimum.
                var minSize = dicomEntries.length === 1 ? SINGLE_FILE_MIN_SIZE : MIN_SERIES_TOTAL_SIZE;
                if (totalSize < minSize) {
                    return reject('That zip is too small to be a CBCT scan.');
                }

                return {
                    kind: 'zip',
                    sourceName: baseName(fileName(file)),
                    fileCount: dicomEntries.length,
                    totalSize: totalSize,
                    files: [],
                    zipFile: file
                };
            });
        }

        // Public API. `files` is a FileList/array from a hidden input.
        //   - webkitdirectory input -> many files with webkitRelativePath -> directory
        //   - accept=".zip" input   -> one .zip file  -> zip
        //                            -> one .dcm/other -> single multi-frame DICOM
        // Resolves with the summary object; rejects with an Error whose .message
        // is safe to show via Notification.error.
        function validate(files) {
            var list = files ? Array.prototype.slice.call(files) : [];
            if (list.length === 0) {
                return reject('No files were selected.');
            }

            // Directory selection: more than one file, or a file carrying a
            // webkitRelativePath (webkitdirectory always sets it).
            var looksLikeDirectory = list.length > 1 ||
                (list[0].webkitRelativePath && list[0].webkitRelativePath.indexOf('/') !== -1);
            if (looksLikeDirectory) {
                return validateDirectory(list);
            }

            var only = list[0];
            if (ZIP_EXT_RE.test(only.name)) {
                return validateZip(only);
            }
            // A lone non-zip file is treated as a single multi-frame DICOM,
            // whether it is a .dcm, .dicom, or extensionless slice — the content
            // check inside validateSingle is what actually decides.
            return validateSingle(only);
        }

        return {
            validate: validate,
            // Exposed for DCL-212/214 and for any reuse/testing.
            parseZipCentralDirectory: parseZipCentralDirectory,
            thresholds: {
                MIN_SERIES_FILE_COUNT: MIN_SERIES_FILE_COUNT,
                MAX_SERIES_FILE_COUNT: MAX_SERIES_FILE_COUNT,
                SINGLE_FILE_MIN_SIZE: SINGLE_FILE_MIN_SIZE,
                MIN_SERIES_TOTAL_SIZE: MIN_SERIES_TOTAL_SIZE,
                MIN_ZIP_SIZE: MIN_ZIP_SIZE,
                MAX_TOTAL_SIZE: MAX_TOTAL_SIZE,
                DIRECTORY_SAMPLE_SIZE: DIRECTORY_SAMPLE_SIZE,
                DIRECTORY_DICOM_MAJORITY: DIRECTORY_DICOM_MAJORITY
            }
        };
    }]);
